// Imports
import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";
import { PCA } from 'ml-pca';
//import Select from 'react-select';

// Constants
const BABY_BLUE = "#89CFF0";
const DEFAULT_CAMERA_POS = [0, 0, 40];
//const GENIUS_TOKEN = process.env.GENIUS_TOKEN;

// PulseText for loading
function PulseText({ children, speed = 1 }) {
    const [time, setTime] = useState(0);

    useEffect(() => {
    let frameId;
    const animate = () => {
        setTime(prev => prev + 0.02 * speed);
        frameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frameId);
    }, [speed]);

    // Opacity oscillates between 0.25 and 1
    const opacity = 0.625 + 0.375 * Math.sin(time * Math.PI);
    // Scale oscillates between 1 and 1.05 (or adjust as desired)
    //const scale = 1 + 0.05 * Math.sin(time * Math.PI);

    return (
    <div style={{
        opacity,
    }}>
        {children}
    </div>
    );
}

// Adding a browser size listener for dynamic browser resizing
function useWindowSize() {

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
    const handleResize = () => {
        setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
        });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
}

// Block all scroll events not targeting the canvas or about section
function useBlockPageScroll(canvasId = 'orbit-canvas') {
    useEffect(() => {
    const blockEvent = e => {
        // Allow if target is within the canvas OR about section
        if (
        e.target.closest(`#${canvasId}`) ||
        e.target.closest(`#about-section`)
        ) {
        return;
        }
        e.preventDefault();
        e.stopPropagation();
    };

    window.addEventListener("wheel", blockEvent, { passive: false });
    window.addEventListener("touchmove", blockEvent, { passive: false });
    window.addEventListener("keydown", blockEvent);

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
        window.removeEventListener("wheel", blockEvent);
        window.removeEventListener("touchmove", blockEvent);
        window.removeEventListener("keydown", blockEvent);
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
    };
    }, [canvasId]);
}

// Double-tap on mobile
function useDoubleTap(callback, ms = 350, canvasId = 'orbit-canvas') {
    useEffect(() => {
    let lastTap = 0;
    let lastTouchEndTime = 0;
    let touchMoved = false;

    const handleTouchStart = (e) => {
        touchMoved = false;
        // Only care about single-finger taps
        if (e.touches.length > 1) touchMoved = true;
        // Save start position to check for movement
        e.target.dataset.startX = e.touches[0].clientX;
        e.target.dataset.startY = e.touches.clientY;
    };

    const handleTouchMove = (e) => {
        // If movement is more than a few pixels, it's not a tap
        const sx = parseInt(e.target.dataset.startX, 10);
        const sy = parseInt(e.target.dataset.startY, 10);
        const dx = Math.abs(e.touches.clientX - sx);
        const dy = Math.abs(e.touches.clientY - sy);
        if (dx > 10 || dy > 10) touchMoved = true;
    };

    const handleTouchEnd = (e) => {
        // If not one finger, or moved, do nothing
        if (touchMoved || e.changedTouches.length !== 1) return;

        const now = Date.now();
        if (now - lastTap < ms && now - lastTouchEndTime > 40) {
        callback(e);
        lastTap = 0;
        } else {
        lastTap = now;
        }
        lastTouchEndTime = now;
    };

    const el = document.getElementById(canvasId);
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart);
    el.addEventListener('touchmove', handleTouchMove);
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
        el.removeEventListener('touchstart', handleTouchStart);
        el.removeEventListener('touchmove', handleTouchMove);
        el.removeEventListener('touchend', handleTouchEnd);
    };
    }, [callback, ms, canvasId]);
}  

// Main MusicViz function
function MusicViz() {
    const navigate = useNavigate();
    useBlockPageScroll('orbit-canvas');
    
    const [positions, setPositions] = useState([]);
    const [words, setWords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [autoRotate, setAutoRotate] = useState(true);
    //const [showReset, setShowReset] = useState(false);

    // Title state
    const [isTitleCollapsed, setIsTitleCollapsed] = useState(false);

    // Selected song
    //const [selectedSong, setSelectedSong] = useState(null);

    // Menu state
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('Controls');

    // Broswer orientation
    const { width, height } = useWindowSize();
    //const isWide = width > 900 || width > height; // 800 = infinite craft
    const isMobile = window.matchMedia('(pointer: coarse)').matches;

    // To dynamically center loading box and instructions
    const titleRef = useRef();
    const menuButtonRef = useRef();
    const [centerY, setCenterY] = useState(null);

    useEffect(() => {
    function updateCenterY() {
        if (titleRef.current && menuButtonRef.current) {
        const titleRect = titleRef.current.getBoundingClientRect();
        const menuRect = menuButtonRef.current.getBoundingClientRect();
        const mid = (titleRect.bottom + menuRect.top) / 2;
        setCenterY(mid);
        }
    }
    updateCenterY();
    window.addEventListener('resize', updateCenterY);
    return () => window.removeEventListener('resize', updateCenterY);
    }, [isTitleCollapsed, width, height]);

    // For more albums, just add more entries here:
    const ALBUMS = [
        {
        value: "stick_season",
        label: "Noah Kahan - Stick Season (entire Deluxe Album)",
        json: "/MusicViz/stick_season_wordvecs.json"
        },
        {
            value: "hailey_wordvec",
            label: "Justin Bieber - Hailey",
            json: "/MusicViz/hailey_wordvecs.json"
        },
        {
            value: "uh_oh_wordvec",
            label: "Tate McRae - uh oh",
            json: "/MusicViz/_uh_oh_wordvecs.json"
        },
        /* {
            value: "marshall_mathers_lp_wordvec",
            label: "Eminem - The Marshall Mathers LP",
            json: "/MusicViz/marshall_mathers_lp_wordvecs.json"
        } */
        // Example for adding another album:
        // {
        //   value: "another_album",
        //   label: "Artist - Album Name",
        //   json: "/MusicViz/another_album_wordvecs.json"
        // }
    ];

    const [selectedAlbum, setSelectedAlbum] = useState(ALBUMS[0].value);

    // Extract album object for the selected value
    const activeAlbum = ALBUMS.find(a => a.value === selectedAlbum);

    // To reset camera
    const controlsRef = useRef();
    // Rotating group ref
    const groupRef = useRef();

    // Menu handlers
    const handleMenuToggle = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleMenuOutsideClick = (e) => {
    if (e.target.classList.contains('menu-overlay')) {
        setIsMenuOpen(false);
    }
    };

    // Handles loading & visualizing new album
    async function loadAlbum(albumObj = activeAlbum) {
        setLoading(true);

        // Fetch the mini vectors JSON
        const resp = await fetch(albumObj.json);
        const vectorsObj = await resp.json(); // { word: [vector, ...] }

        // Keys are unique words, values are 300d vectors
        const newWords = Object.keys(vectorsObj);
        const vectors = newWords.map(w => vectorsObj[w]);

        // PCA to 3D
        if (vectors.length < 10) {
        setWords([]);
        setPositions([]);
        setLoading(false);
        return;
        }
        const pca = new PCA(vectors);
        const comps = pca.predict(vectors, { nComponents: 3 }).to2DArray();
        const SPREAD = 9; // or 10, adjust as you like
        const arr = comps.map(([x, y, z]) => [x * SPREAD, y * SPREAD, z * SPREAD]);

        setWords(newWords);
        setPositions(arr);
        setLoading(false);
    }

    // Scroll to top on first navigation or refresh
    /* useEffect(() => {
        // Temporarily re-allow scrolling (helps on iOS/Safari)
        const originalOverflow = document.body.style.overflow;
        const originalTouchAction = document.body.style.touchAction;
        document.body.style.overflow = "auto";
        document.body.style.touchAction = "auto";
    
        // Next animation frame, scroll to top, then restore overflow lock
        requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    
        // Lock scroll again AFTER resetting position
        document.body.style.overflow = originalOverflow;
        document.body.style.touchAction = originalTouchAction;
        });
    }, []); */

    // Load album on first mount and when dropdown changes - I don't want this, only when the user presses Add
    /* useEffect(() => {
        loadAlbum();
        // eslint-disable-next-line
    }, [selectedAlbum]); */

    // Allow for double-tap on mobile
    useDoubleTap(handleReset, 350, 'orbit-canvas');

    // Handle user drag interaction: disables auto-rotation, shows reset icon
    function handleDragStart() {
        setAutoRotate(false);
        //setShowReset(true);
    }

    // Clicking reset: enables auto-rotation, resets controls
    function handleReset() {
        setAutoRotate(true);
        //setShowReset(false);
        if (controlsRef.current) {
        // Reset to default position/rotation
        controlsRef.current.reset();
        }
    }

    return (
        <div
        style={{
            position: "fixed", // important! prevents parent growing
            top: "env(safe-area-inset-top, 0px)",
            bottom: "env(safe-area-inset-bottom, 0px)",
            left: "env(safe-area-inset-left, 0px)",
            right: "env(safe-area-inset-right, 0px)",
            width: "auto",
            height: "auto",
            minWidth: 0,
            minHeight: 0,
            background: "rgba(0,0,0,0.75)",
            overflow: "hidden",
            boxSizing: "border-box",
            userSelect: "none",
            WebkitUserSelect: "none",
            msUserSelect: "none",
            MozUserSelect: "none",
        }}
        >
            {/* Top Bar: Title, Dropdown, Add Button */}
            <div
            ref={titleRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'absolute',
                top: `calc(25px + env(safe-area-inset-top, 0px))`,
                left: 25,
                zIndex: 10,
                padding: isTitleCollapsed ? 0 : 12,
                background: "rgba(0,0,0,0.8)",
                color: '#333',
                border: isTitleCollapsed ? 'none' : '2px solid #ddd',
                borderRadius: '8px',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: width - 75,
                minWidth: isTitleCollapsed ? '60px' : 'unset',
                minHeight: isTitleCollapsed ? '60px' : 'unset',
                transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
            }}
            >
            {isTitleCollapsed ? (
                <button
                onClick={() => setIsTitleCollapsed(false)}
                style={{
                    background: "linear-gradient(90deg,#89CFF0,#69b3e7)",
                    border: "none",
                    borderRadius: "8px",
                    fontFamily: "'Sniglet', sans-serif",
                    fontSize: "60px",
                    color: "#333",
                    width: "60px",
                    height: "60px",
                    margin: "0 auto",
                    fontWeight: 800,
                    cursor: "pointer",
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    transition: "transform 0.15s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                title="Expand"
                >
                L
                </button>
            ) : (
                <>
                <span
                    style={{
                    fontFamily: "'Sniglet', sans-serif",
                    fontWeight: 800,
                    fontSize: width < 900 ? Math.min(36, width * 0.08) : 36,
                    color: 'white',
                    margin: '6px 0 18px 6px',
                    letterSpacing: "0.03em",
                    }}
                >
                    Lyric Explorer
                </span>
                <form
                    onSubmit={e => { e.preventDefault(); loadAlbum(); }}
                    style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, marginLeft: 6 }}>
                    <label htmlFor="album" style={{
                        color: BABY_BLUE,
                        }}>Song:</label>
                    <select
                    id="album"
                    value={selectedAlbum}
                    onChange={e => setSelectedAlbum(e.target.value)}
                    style={{
                        color: BABY_BLUE,
                        background: "black",
                        border: "1px solid " + BABY_BLUE,
                        borderRadius: 4,
                        minWidth: 80,
                        maxWidth: '50%',
                        width: width < 900 ? 150 : 200,
                        cursor: 'pointer',
                    }}>
                    {ALBUMS.map(album =>
                        <option value={album.value} key={album.value}>{album.label}</option>
                    )}
                    </select>
                    {/* <Select
                        id="album"
                        options={ALBUMS}
                        value={ALBUMS.find(option => option.value === selectedAlbum)}
                        onChange={option => setSelectedAlbum(option.value)}
                        styles={{
                            container: (base) => ({
                            ...base,
                            minWidth: 80,
                            maxWidth: '50%',
                            marginRight: 8
                            }),
                            control: (base) => ({
                            ...base,
                            background: "black",
                            borderColor: BABY_BLUE,
                            borderRadius: 4,
                            color: BABY_BLUE,
                            }),
                            menu: (base) => ({
                            ...base,
                            background: "#111c",
                            color: BABY_BLUE,
                            borderRadius: 8,
                            overflow: "hidden",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            minWidth: 80,
                            maxWidth: '100%',
                            }),
                            option: (base) => ({
                            ...base,
                            color: BABY_BLUE,
                            background: "transparent",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            }),
                            singleValue: (base) => ({
                            ...base,
                            color: BABY_BLUE,

                            }),
                        }}
                        theme={theme => ({
                            ...theme,
                            colors: {
                            ...theme.colors,
                            primary25: "#222b36",
                            primary: BABY_BLUE,
                            neutral0: "black",
                            neutral20: BABY_BLUE,
                            }
                        })}
                    /> */}
                    <button type="submit" style={{
                    marginLeft: 8,
                    background: BABY_BLUE,
                    color: "black",
                    borderRadius: 4,
                    border: "none",
                    padding: "0.3em 1.2em",
                    fontFamily: "'Sniglet', sans-serif",
                    fontWeight: 400,
                    cursor: 'pointer',
                    }}>
                    Add
                    </button>
                </form>
                <div style={{
                    margin: "4px 0 12px 6px",
                    fontSize: 13,
                    color: BABY_BLUE,
                    }}>
                    {loading ? "Loading…" : `Words Visualized: ${words.length}`}
                </div>
                {/* Minimize button absolutely positioned flush to bottom right */}
                <span
                    onClick={() => setIsTitleCollapsed(true)}
                    style={{
                    position: "absolute",
                    right: "6px",
                    bottom: "6px",
                    background: "rgba(50,50,50,0.85)",
                    border: "none",
                    borderRadius: "7px",
                    padding: "0px 8px",
                    fontSize: "18px",
                    fontFamily: 'Nunito',
                    fontWeight: 900,
                    color: "#89CFF0",
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    transition: "background 0.18s, color 0.15s, box-shadow 0.16s",
                    boxShadow: "none",
                    }}
                    onMouseEnter={e => {
                    e.currentTarget.style.background = "#444";
                    e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={e => {
                    e.currentTarget.style.background = "rgba(50,50,50,0.85)";
                    e.currentTarget.style.color = "#89CFF0";
                    }}
                    title="Collapse"
                >
                    –
                </span>
                </>
            )}
            </div>

            {/* Menu Button - Bottom Left Corner */}
            <button
            ref={menuButtonRef}
            onClick={(e) => {
                e.stopPropagation();
                handleMenuToggle();
            }}
            style={{
                position: 'absolute',
                bottom: `calc(25px + env(safe-area-inset-bottom, 0px))`,
                left: 25,
                zIndex: 20,
                fontSize: '18px',
                padding: '12px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#333',
                border: '2px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            >
                🤔&nbsp;&nbsp;Confused?
            </button>

            {/* Menu Overlay */}
            {isMenuOpen && (
                <div 
                className="menu-overlay"
                onClick={handleMenuOutsideClick}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: width,
                    height: height,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 50,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                >
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    minWidth: width * 0.2,
                    width: 'auto',
                    maxWidth: width * 0.9,
                    maxHeight: '90vh',
                    position: 'relative',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    overflow: 'hidden', // Prevents visible negative space in corners
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Header with tabs and close button */}
                    <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 32px 0 32px',
                    borderBottom: '1px solid #eee'
                    }}>
                    <div style={{
                        display: 'flex',
                        gap: '0px',
                        fontFamily: "'Sniglet', sans-serif",
                        fontSize: width < 900 ? Math.min(18, width * 0.03) : 18,
                    }}>
                        {['Controls', 'What is this?', '???'].map((tab, index) => (
                        <React.Fragment key={tab}>
                            <span
                            onClick={() => handleTabClick(tab)}
                            style={{
                                cursor: 'pointer',
                                color: activeTab === tab ? 'black' : '#999',
                                fontWeight: 400,
                                transition: 'color 0.2s ease',
                                WebkitTapHighlightColor: 'transparent',
                            }}
                            onMouseEnter={(e) => e.target.style.color = 'black'}
                            onMouseLeave={(e) => e.target.style.color = activeTab === tab ? 'black' : '#999'}
                            >
                            {tab}
                            </span>
                            {index < 3 && <span style={{ color: '#ccc', margin: '0 12px' }}>|</span>}
                        </React.Fragment>
                        ))}
                    </div>
                    <span
                        onClick={() => setIsMenuOpen(false)}
                        style={{
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#999',
                        transition: 'color 0.2s ease',
                        padding: '4px',
                        marginLeft: '32px',
                        WebkitTapHighlightColor: 'transparent',
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'black'}
                        onMouseLeave={(e) => e.target.style.color = '#999'}
                    >
                        ×
                    </span>
                    </div>

                    {/* Tab Content */}
                    <div style={{
                    padding: '20px',
                    maxHeight: 'calc(90vh - 100px)',
                    overflowY: 'auto'
                    }}>
                    {activeTab === 'Controls' && (
                        <div style={{
                            fontFamily: "'Sniglet', sans-serif",
                            fontWeight: 400,
                            }}>
                        {!isMobile ? (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Left click + drag</span>
                                    <span style={{ color: '#999' }}>Rotate</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Scroll wheel</span>
                                    <span style={{ color: '#999' }}>Zoom</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Right click + drag</span>
                                    <span style={{ color: '#999' }}>Pan</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Double click</span>
                                    <span style={{ color: '#999' }}>Reset zoom</span>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Swipe</span>
                                    <span style={{ color: '#999' }}>Rotate</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Pinch</span>
                                    <span style={{ color: '#999' }}>Zoom</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Two-finger swipe</span>
                                    <span style={{ color: '#999' }}>Pan</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ color: 'black' }}>Double tap</span>
                                    <span style={{ color: '#999' }}>Reset zoom</span>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={() => navigate('/')}
                            style={{
                            fontSize: '16px',
                            fontFamily: "'Sniglet', sans-serif",
                            fontWeight: 400,
                            padding: '10px 20px',
                            backgroundColor: '#E3242B',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            width: 'fit-content',
                            margin: '20px auto 0 auto',
                            display: 'block',
                            }}
                        >
                            Quit
                        </button>
                        </div>
                    )}

                    {activeTab === 'What is this?' && (
                        <div 
                        id="about-section"
                        style={{
                        fontFamily: "'Sniglet', sans-serif",
                        fontSize: '16px',
                        lineHeight: '1.6',
                        whiteSpace: 'normal',
                        maxWidth: '520px',
                        padding: "10px 20px 20px 20px",
                        maxHeight: 'calc(90vh - 100px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
                        overflowY: 'auto',
                        }}>
                        <p>
                            Hi, I'm Reilly! I love building things and bringing ideas to life.
                            <br />
                            <br />
                            Lyric Explorer allows you to visualize album lyrics in the form of a word map.
                            The closer two words are on the map, the closer they are in meaning. You can 
                            move around on the map and even identify recurring themes / sub-regions.
                            <br />
                            <br />
                            This project uses word vectorization via GloVe to maintain the same global map,
                            as well as PCA compression to reduce high dimensionality into something more
                            friendly to the human eye.
                            <br />
                            <br />
                            The idea for Lyric Explorer sprouted alongside my obsession with Noah Kahan's
                            <i>Stick Season</i> album. I came for the self-deprecating humor and stayed for
                            the homages to remote, cozy villages. But like most, I wanted more. So I made
                            Lyric Explorer to search for themes in the words he sang, hoping that they might
                            get me closer to the essence of small-town suburbias. You tell me if it worked 🙃
                            <br />
                            <br />
                            Hope you enjoy, and let me know what albums I should add next! Feel free to reach out
                            with any suggestions or feedback as well.
                            <br />
                            <br />For more little creations, check out{' '}
                            <a 
                            href="https://reilly.fun" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: "#69b3e7", fontFamily: "'Fredoka One', cursive" }}
                            >
                            reilly.fun
                            </a> 🤠
                        </p>
                        </div>
                    )}

                    {activeTab === '???' && (
                        <div style={{
                            fontFamily: "'Sniglet', sans-serif",
                            textAlign: 'center',
                            color: '#999',
                            fontSize: '16px',
                            padding: '40px 0',
                            }}>
                            <p>Coming soon!</p>
                        </div>
                    )}
                    </div>
                </div>
                </div>
            )}

            {/* Beginning instructions text */}
            {!loading && words.length === 0 && (
            <div
                style={{
                position: "absolute",
                top: `${centerY}px`,
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "white",
                fontSize: 22,
                textAlign: "center",
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                zIndex: 30,
                pointerEvents: "none", // so users can't accidentally interact/block hover events below
                width: "90vw",
                maxWidth: 400,
                lineHeight: 1.5,
                }}
            >
                <PulseText speed={0.5}>To begin, pick an album and click <i>Add</i></PulseText>
            </div>
            )}

            {/* Loading spinner */}
            {loading && (
            <div
                style={{
                position: "absolute",
                top: `${centerY}px`,
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 30,
                pointerEvents: "none",
                width: "90vw",
                maxWidth: 400,
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
                }}
            >
                <div className="ring-spinner-bg">
                <div className="ring-spinner">
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                    <div className="ring-spinner-dot"></div>
                </div>
                </div>
            </div>
            )}

            <Canvas
                id="orbit-canvas"
                camera={{ position: DEFAULT_CAMERA_POS, fov: 70 }}
                onDoubleClick={handleReset}
                style={{
                    width: "100vw",
                    height: "100vh",
                    background: "black",
                }}
            >
                <ambientLight intensity={0.8} />
                <directionalLight position={[0, 10, 5]} intensity={0.3} />
                {/* Handle rotation group */}
                <RotatingCloud
                groupRef={groupRef}
                autoRotate={autoRotate}
                positions={positions}
                words={words}
                />
                {/* Orbit controls, call handleDragStart on interaction */}
                <OrbitControls
                ref={controlsRef}
                enablePan
                enableRotate
                enableZoom
                onStart={handleDragStart}
                />
            </Canvas>
        </div>
    );
}

// Wraps the points so we can rotate the whole cluster
function RotatingCloud({ groupRef, autoRotate, positions, words }) {
    useFrame((_, delta) => {
        if (autoRotate && groupRef.current) {
        groupRef.current.rotation.y += delta * 0.0; // Adjust for desired speed // Making no rotation until I can truly figure out how to keep the labels forward
        }
    });
    return (
        <group ref={groupRef}>
        {positions.map((pos, i) => (
            <WordPoint key={i} position={pos} text={words[i]} />
        ))}
        </group>
    );
}

// Point + label
function WordPoint({ position = [0, 0, 0], text }) {
    const textRef = useRef();
    const { camera } = useThree();

    useFrame(() => {
    if (textRef.current) {
        // Always face the camera in world space
        textRef.current.quaternion.copy(camera.quaternion);
        // Offset label to the right of the sphere, relative to camera
        const right = new THREE.Vector3();
        camera.getWorldDirection(right);
        right.cross(camera.up).normalize();
        right.multiplyScalar(0.35); // adjust offset as desired
        textRef.current.position.set(right.x, right.y, right.z);
    }
    });

    return (
    <group position={position}>
        <mesh>
        <sphereGeometry args={[0.18, 20, 20]} />
        <meshStandardMaterial color={BABY_BLUE} emissive={BABY_BLUE} emissiveIntensity={0.6} />
        </mesh>
        <Text
        ref={textRef}
        color={BABY_BLUE}
        fontFamily= "'Sniglet', sans-serif"
        fontSize={0.32}
        anchorX="left"
        anchorY="middle"
        outlineColor="black"
        outlineWidth={0.01}
        // position is set dynamically via useFrame!
        >
        {text}
        </Text>
    </group>
    );
}

export default MusicViz;